import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Clock, ShieldCheck, LogIn } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 15000;
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function useMaintenance() {
  const [state, setState] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get("/maintenance/status"); setState(r.data); } catch { /* keep last known */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("ss:maintenance", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); window.removeEventListener("ss:maintenance", onFocus); };
  }, [load]);
  return [state, load];
}

// Full-screen page shown to everyone who isn't an admin while maintenance is on.
export function MaintenancePage({ message, signedOut = false }) {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="maintenance-page">
      <div className="max-w-xl w-full text-center">
        <p className="font-heading text-3xl text-cocoa tracking-tight">Sugar<span className="italic font-medium text-mauve">Sense</span></p>
        <p className="mt-10 text-[11px] tracking-[0.45em] uppercase text-gold">A little quiet time</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl text-cocoa leading-tight">
          {signedOut ? "Thank you — your work is safe." : "We're putting the finishing touches on something."}
        </h1>
        <p className="mt-5 text-base sm:text-lg text-cocoa/75 leading-relaxed" data-testid="maintenance-message">{message}</p>
        {signedOut && (
          <p className="mt-3 text-sm text-cocoa/60">You've been signed out for the update. Anything you were writing will be waiting in the box when you come back.</p>
        )}
        <div className="mt-8 inline-flex items-center gap-2 text-sm text-cocoa/60">
          <Sparkles size={14} className="text-gold" /> This page refreshes itself — no need to reload.
        </div>
        <div className="mt-12">
          {pathname === "/login"
            ? null
            : <Link to="/login" data-testid="maintenance-admin-login" className="text-xs text-cocoa/40 hover:text-cocoa inline-flex items-center gap-1"><LogIn size={12} /> Team sign-in</Link>}
        </div>
      </div>
    </div>
  );
}

// Slim banners: countdown for members in the grace window, status strip for admins.
export function MaintenanceBanner({ state, user }) {
  const isAdmin = user?.role === "admin";
  const [secs, setSecs] = useState(state.seconds_until_lockout ?? 0);
  useEffect(() => { setSecs(state.seconds_until_lockout ?? 0); }, [state.seconds_until_lockout, state.server_time]);
  useEffect(() => {
    if (isAdmin) return;
    const id = setInterval(() => setSecs((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [isAdmin]);

  if (isAdmin) {
    return (
      <div className="bg-cocoa text-ivory text-xs sm:text-sm px-4 py-2 flex items-center justify-center gap-3 flex-wrap" data-testid="maintenance-admin-banner">
        <ShieldCheck size={14} className="text-gold" />
        <span>Maintenance mode is <strong>ON</strong> — members {state.locked ? "are locked out" : `will be signed out at ${new Date(state.lockout_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}.</span>
        <Link to="/admin-ss/maintenance" className="underline text-gold">Manage</Link>
      </div>
    );
  }
  const urgent = secs <= 60;
  return (
    <div className={`${urgent ? "bg-rose text-ivory" : "bg-gold/20 text-cocoa"} text-sm px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap`} role="status" aria-live="polite" data-testid="maintenance-countdown">
      <Clock size={15} className={urgent ? "text-ivory" : "text-gold"} />
      <span>A quick update is coming. You'll be signed out in <strong className="font-mono tabular-nums" data-testid="maintenance-countdown-time">{fmt(secs)}</strong> — finish up and submit what you're working on. Drafts are saved automatically.</span>
    </div>
  );
}

// Wraps the routed app: decides between the maintenance page, a banner, or nothing.
export function MaintenanceGate({ children }) {
  const [state] = useMaintenance();
  const { user, ready, logout } = useAuth();
  const { pathname } = useLocation();
  const [bootedOut, setBootedOut] = useState(false);
  const bootingRef = useRef(false);

  const isAdmin = user?.role === "admin";
  const enabled = !!state?.enabled;
  const locked = !!state?.locked;

  // Member grace window ended → sign out cleanly (drafts are already in localStorage).
  useEffect(() => {
    if (!enabled || !locked || !user || isAdmin || bootingRef.current) return;
    bootingRef.current = true;
    (async () => { try { await logout(); } finally { setBootedOut(true); } })();
  }, [enabled, locked, user, isAdmin, logout]);

  // Local tick so lockout happens on time between polls.
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled || locked || !state?.lockout_at) return;
    const ms = Math.max(0, new Date(state.lockout_at).getTime() - Date.now()) + 500;
    const t = setTimeout(() => force((n) => n + 1), ms);
    return () => clearTimeout(t);
  }, [enabled, locked, state?.lockout_at]);
  const lockedNow = enabled && (locked || (state?.lockout_at && new Date(state.lockout_at).getTime() <= Date.now()));

  useEffect(() => { if (!enabled) setBootedOut(false); }, [enabled]);

  if (!enabled || !ready) return children;
  if (isAdmin) return <><MaintenanceBanner state={state} user={user} />{children}</>;
  // Team sign-in stays reachable; everything else is the maintenance page.
  if (pathname === "/login" && !user) return (
    <div data-testid="maintenance-login-shell">
      <div className="bg-cocoa text-ivory text-xs px-4 py-2 text-center" data-testid="maintenance-login-note">Maintenance mode — team sign-in only. {state.message}</div>
      {children}
    </div>
  );
  if (user && !lockedNow) return <><MaintenanceBanner state={state} user={user} />{children}</>;
  return <MaintenancePage message={state.message} signedOut={bootedOut} />;
}
